import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Download,
  FileText,
  Layers,
  Loader2,
  Newspaper,
  Plus,
  Search,
  Settings2,
  Trash2,
  X,
} from 'lucide-react';
import { apiError, companiesAPI, downloadReportPdf, reportsAPI } from '../services/api';
import AddAccountModal from '../components/AddAccountModal';
import AccountSettingsModal from '../components/AccountSettingsModal';
import { usePoll } from '../lib/usePoll';
import {
  Alert,
  Button,
  EmptyState,
  OverflowMenu,
  PageHeader,
  ProgressBar,
  ScoreRing,
  SkeletonRows,
  cx,
  stagger,
} from '../components/ui';

interface ReportSummary {
  _id: string;
  companyId: string;
  companyName: string;
  ticker?: string;
  status: 'pending' | 'complete' | 'failed';
  progress?: { step?: string; percent?: number };
  error?: string;
  // A rewrite of a finished report. There is one report per account per team,
  // so refreshing one edits it rather than adding a second card next to it -
  // and it stays 'complete' and readable the whole time.
  refresh?: { status?: 'pending' | 'failed'; step?: string; percent?: number };
  score?: { value?: number; band?: string; summary?: string };
  context?: { sellerName?: string; priorityTopics?: string[]; topics?: string[] };
  fastFacts?: { industry?: string; headquarters?: string };
  generatedAt: string;
  pdfFileName?: string;
  // Reports are listed org-wide, so each row states who wrote it and whether
  // this viewer is allowed to remove it. The server decides both.
  author?: { _id: string; name: string; email: string } | null;
  isMine?: boolean;
  canDelete?: boolean;
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'info' | 'error' | 'success'; text: string } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // The account whose crawl settings are open. Reports carry only the company
  // id, so the record itself is fetched when the dialog is asked for.
  const [settingsFor, setSettingsFor] = useState<{ _id: string; name: string } | null>(null);

  const [query, setQuery] = useState('');
  const [authorId, setAuthorId] = useState('all');
  const [industry, setIndustry] = useState('all');

  // Typing must not fire a request per keystroke
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Dropdown options come from the whole visible history, not the filtered
  // page, so narrowing the list never removes the option you narrowed by.
  const [options, setOptions] = useState<{
    authors: { _id: string; name: string }[];
    industries: string[];
  }>({ authors: [], industries: [] });
  const [total, setTotal] = useState(0);

  const loadOptions = useCallback(async () => {
    try {
      const res = await reportsAPI.getReportFilters();
      setOptions({ authors: res.data.authors || [], industries: res.data.industries || [] });
    } catch (err) {
      // A missing filter list is not worth an error banner; the list still works
      setOptions({ authors: [], industries: [] });
    }
  }, []);

  const load = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const res = await reportsAPI.getReports({
          ...(search ? { q: search } : {}),
          ...(authorId !== 'all' ? { author: authorId } : {}),
          ...(industry !== 'all' ? { industry } : {}),
        });
        setReports(res.data);
        setTotal(Number(res.headers['x-total-count'] ?? res.data.length));
      } catch (err) {
        // Background polls fail quietly: the list on screen is still good, and
        // a banner over it would be noise the next attempt clears anyway.
        if (!silent) setMessage({ tone: 'error', text: apiError(err, 'Could not load reports') });
      } finally {
        setLoading(false);
      }
    },
    [search, authorId, industry]
  );

  // Refetches whenever a filter changes, because `load` depends on all three
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // Reports generate asynchronously, so keep checking while any are pending
  const hasPending = reports.some(
    (r) => r.status === 'pending' || r.refresh?.status === 'pending'
  );
  usePoll(() => load(true), { active: hasPending });

  const filtersActive = Boolean(query.trim()) || authorId !== 'all' || industry !== 'all';

  const clearFilters = () => {
    setQuery('');
    setAuthorId('all');
    setIndustry('all');
  };

  const download = async (report: ReportSummary) => {
    try {
      setBusyId(report._id);
      await downloadReportPdf(
        report._id,
        report.pdfFileName || `salesmotion-${report.companyName}.pdf`
      );
    } catch (err) {
      setMessage({ tone: 'error', text: 'Download failed — the PDF may still be preparing.' });
    } finally {
      setBusyId(null);
    }
  };

  const checkForNews = async (report: ReportSummary) => {
    try {
      setBusyId(report._id);
      const res = await companiesAPI.refreshData(report.companyId);
      setMessage({
        tone: 'success',
        text: `${report.companyName}: found ${res.data.signalsCreated ?? 0} new updates across ${
          res.data.newsFound ?? 0
        } articles`,
      });
    } catch (err) {
      setMessage({ tone: 'error', text: apiError(err, `Could not refresh ${report.companyName}`) });
    } finally {
      setBusyId(null);
    }
  };

  // The settings dialog needs the pages and tags, which a report summary does
  // not carry - so the company is read on demand rather than fattening every
  // row in the list for a dialog most of them never open.
  const openSettings = async (report: ReportSummary) => {
    try {
      setBusyId(report._id);
      const res = await companiesAPI.getCompany(report.companyId);
      setSettingsFor(res.data);
    } catch (err) {
      setMessage({ tone: 'error', text: apiError(err, 'Could not open the account settings') });
    } finally {
      setBusyId(null);
    }
  };

  // The account goes with the report - there is only ever one report per
  // account, so the server treats deleting it as removing the account. Said
  // plainly up front, because it takes the prospect off the whole team's board.
  const remove = async (report: ReportSummary) => {
    const confirmed = window.confirm(
      `Delete the ${report.companyName} report?\n\n` +
        `This also removes ${report.companyName} from the team's accounts, along with its signals and alerts.`
    );
    if (!confirmed) return;

    try {
      setBusyId(report._id);
      await reportsAPI.remove(report._id);
      setReports((prev) => prev.filter((r) => r._id !== report._id));
      setTotal((prev) => Math.max(0, prev - 1));
      setMessage({
        tone: 'success',
        text: `${report.companyName} removed for the team.`,
      });
      // That may have been the last report by an author or in an industry
      loadOptions();
    } catch (err) {
      setMessage({ tone: 'error', text: apiError(err, 'Could not delete the report') });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Research"
        title="Reports"
        description="Every report your team has created, newest first."
        actions={
          <Button icon={Plus} onClick={() => setModalOpen(true)}>
            New report
          </Button>
        }
      />

      {message && (
        <div className="mb-5">
          <Alert tone={message.tone} onDismiss={() => setMessage(null)}>
            {message.text}
          </Alert>
        </div>
      )}

      {/* Filters. Keyed off the unfiltered total so the bar - and the Clear
          button - survive a search that matches nothing. */}
      {total > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2.5">
          <div className="relative min-w-[200px] flex-1">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint"
            />
            <input
              className="input pl-9"
              placeholder="Search by company name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <select
            className="input w-auto min-w-[170px]"
            value={authorId}
            onChange={(e) => setAuthorId(e.target.value)}
            aria-label="Filter by who created the report"
          >
            <option value="all">Anyone on the team</option>
            {options.authors.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name}
              </option>
            ))}
          </select>

          <select
            className="input w-auto min-w-[170px]"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            aria-label="Filter by industry"
            disabled={options.industries.length === 0}
          >
            <option value="all">All industries</option>
            {options.industries.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>

          {filtersActive && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] font-semibold text-ink-muted transition hover:bg-slate-100 hover:text-ink"
            >
              <X size={14} /> Clear
            </button>
          )}

          <span className="ml-auto shrink-0 text-[13px] font-medium text-ink-muted">
            {filtersActive ? `${reports.length} of ${total}` : `${total} reports`}
          </span>
        </div>
      )}

      {loading ? (
        <SkeletonRows rows={3} />
      ) : reports.length === 0 && filtersActive ? (
        <EmptyState
          icon={Search}
          title="Nothing matches those filters"
          description="No report on your team matches what you are looking for."
          action={
            <Button variant="secondary" icon={X} onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : reports.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No reports yet"
          description="Add a company and we will research it, score how well it fits what you sell, and write the report for you."
          action={
            <Button icon={Plus} onClick={() => setModalOpen(true)}>
              Create your first report
            </Button>
          }
        />
      ) : (
        <div className="stagger grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {reports.map((report, i) => {
            const busy = busyId === report._id;
            const done = report.status === 'complete';

            return (
              <div
                key={report._id}
                style={stagger(i)}
                className={cx('card flex flex-col overflow-hidden', done && 'card-interactive')}
                onClick={() => done && navigate(`/reports/${report._id}`)}
              >
                <div className="flex-1 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-base font-bold tracking-tight text-ink">
                          {report.companyName}
                        </h2>
                        {report.ticker && (
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-2xs font-semibold text-ink-muted">
                            {report.ticker}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-[13px] text-ink-muted">
                        {[report.fastFacts?.industry, report.fastFacts?.headquarters]
                          .filter(Boolean)
                          .join(' · ') || 'Company report'}
                      </p>
                      {report.context?.sellerName && (
                        <p className="mt-1.5 flex items-center gap-1.5 text-2xs text-ink-muted">
                          <Layers size={11} className="shrink-0 text-ink-faint" />
                          <span className="truncate font-medium">
                            Written for {report.context.sellerName}
                          </span>
                        </p>
                      )}
                      {report.author && !report.isMine && (
                        <p className="mt-1 truncate text-2xs text-ink-faint">
                          Created by {report.author.name}
                        </p>
                      )}
                    </div>

                    {done && report.score?.value ? (
                      <ScoreRing value={report.score.value} size={52} />
                    ) : report.status === 'pending' ? (
                      <Loader2 size={18} className="mt-1 shrink-0 animate-spin text-brand-500" />
                    ) : (
                      <AlertTriangle size={18} className="mt-1 shrink-0 text-red-500" />
                    )}
                  </div>

                  {/* Lens — high-priority topics lead, so they are shown first */}
                  {report.context?.priorityTopics?.length || report.context?.topics?.length ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(report.context.priorityTopics || []).slice(0, 3).map((topic) => (
                        <span
                          key={topic}
                          title="Top priority"
                          className="rounded-md bg-emerald-50 px-2 py-0.5 text-2xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200"
                        >
                          {topic}
                        </span>
                      ))}
                      {(report.context.topics || [])
                        .slice(0, Math.max(0, 3 - (report.context.priorityTopics?.length || 0)))
                        .map((topic) => (
                          <span
                            key={topic}
                            className="rounded-md bg-brand-50 px-2 py-0.5 text-2xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-200"
                          >
                            {topic}
                          </span>
                        ))}
                    </div>
                  ) : null}

                  {done && report.score?.summary && (
                    <p className="mt-3 line-clamp-3 text-[13px] leading-relaxed text-ink-soft">
                      {report.score.summary}
                    </p>
                  )}

                  {report.status === 'pending' && (
                    <div className="mt-4">
                      <ProgressBar percent={report.progress?.percent} />
                      <p className="mt-2 text-2xs font-medium text-ink-muted">
                        {report.progress?.step || 'Waiting to start'}
                      </p>
                    </div>
                  )}

                  {/* Being rewritten with fresh research. What is summarised
                      above is the previous version and stays readable. */}
                  {report.status !== 'pending' && report.refresh?.status === 'pending' && (
                    <div className="mt-4">
                      <ProgressBar percent={report.refresh.percent} />
                      <p className="mt-2 text-2xs font-medium text-brand-600">
                        Refreshing — {report.refresh.step || 'getting started'}
                      </p>
                    </div>
                  )}

                  {report.status === 'failed' && (
                    <p className="mt-3 line-clamp-2 rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700">
                      {report.error || 'This report did not finish'}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 border-t border-slate-100 bg-surface-2 px-4 py-2.5">
                  <span className="text-2xs text-ink-faint">
                    {new Date(report.generatedAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>

                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    {done && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/reports/${report._id}`)}
                      >
                        Open <ArrowRight size={13} />
                      </Button>
                    )}

                    {busy && <Loader2 size={14} className="animate-spin text-brand-500" />}

                    <OverflowMenu
                      label={`Actions for the ${report.companyName} report`}
                      actions={[
                        {
                          label: 'Download PDF',
                          icon: Download,
                          hidden: !done,
                          disabled: busy,
                          onClick: () => download(report),
                        },
                        {
                          // Same set as the account menu. Rewriting is not
                          // here either - it belongs on the open report.
                          label: 'Check for news',
                          icon: Newspaper,
                          disabled: busy,
                          onClick: () => checkForNews(report),
                        },
                        {
                          label: 'Account settings',
                          icon: Settings2,
                          disabled: busy,
                          onClick: () => openSettings(report),
                        },
                        {
                          // Named for what it actually does: the account is
                          // removed with the report, since there is one report
                          // per account and an empty row would only invite a
                          // second Generate.
                          label: 'Delete report & account',
                          icon: Trash2,
                          danger: true,
                          // The account's owner, or an owner/admin. The server
                          // decides and enforces it; this only renders it.
                          hidden: report.status === 'pending' || report.canDelete === false,
                          disabled: busy,
                          hint: report.isMine
                            ? 'Removes the account for the team'
                            : `Written by ${report.author?.name || 'a teammate'} — removes the account for the team`,
                          onClick: () => remove(report),
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AccountSettingsModal
        open={Boolean(settingsFor)}
        account={settingsFor}
        onClose={() => setSettingsFor(null)}
        onSaved={() => {
          setMessage({
            tone: 'success',
            text: `${settingsFor?.name} settings saved. They take effect on the next report or refresh.`,
          });
        }}
      />

      <AddAccountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdded={({ companyName, reportId, reportError, alreadyTracked, addedByName }) => {
          load(true);
          loadOptions();

          // No second card ever appears for an account the team already has -
          // the report it already holds is rewritten and opened instead
          const added = alreadyTracked
            ? `${companyName} is already tracked${addedByName ? ` (added by ${addedByName})` : ''} — refreshing it`
            : `${companyName} added`;

          if (reportError) {
            setMessage({ tone: 'error', text: `${added}, but: ${reportError.error}` });
          } else if (reportId) {
            navigate(`/reports/${reportId}`);
          } else {
            setMessage({ tone: 'success', text: `${added}.` });
          }
        }}
      />
    </div>
  );
}
